import {
  useCreateIngredient,
  useUpdateIngredient,
} from "@/services/ingredients/mutation";
import { Ingredient } from "@/types/ingredient";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
} from "antd";
import { forwardRef, useImperativeHandle, useState } from "react";

export interface ModalCUIngredientRef {
  childFunction: (register?: Ingredient) => void;
}

export interface ModalCUIngredientProps {
  categories?: { id: string; name: string }[];
}

const optionsTypes = [
  {
    label: "Gramos (g)",
    value: "g",
  },
  {
    label: "Kilogramos (kg)",
    value: "kg",
  },
  {
    label: "Mililitros (ml)",
    value: "ml",
  },
  {
    label: "Litros (L)",
    value: "l",
  },
  {
    label: "Unidad",
    value: "unit",
  },
  {
    label: "Botella",
    value: "bottle",
  },
  {
    label: "Lata",
    value: "can",
  },
];

export const ModalCUIngredient = forwardRef<
  ModalCUIngredientRef,
  ModalCUIngredientProps
>((props, ref) => {
  const { categories = [] } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [id, setId] = useState<string>("");
  const [isEdit, setIsEdit] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  /* const [removeImage, setRemoveImage] = useState<1 | 0>(0);
    const [fileList, setFileList] = useState([]); */

  const [form] = Form.useForm();

  const [alert, setAlert] = useState({
    visible: false,
    description: "",
  });

  const childFunction = (register?: Ingredient) => {
    setOpen(true);
    setAlert({ visible: false, description: "" });
    setIsEdit(false);

    if (register) {
      setId(register.id);
      setIsEdit(true);
      form.setFieldsValue({
        name: register.name,
        unit: register.unit,
        stock: register.stock,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        availability_rules: [],
        price_rules: [],
      });
    }
  };

  useImperativeHandle(ref, () => ({
    childFunction,
  }));

  const createIngredientMutation = useCreateIngredient();
  const updateIngredientMutation = useUpdateIngredient();

  const handleError = (error: any) => {
    console.log(error);
    let error_message = "Ups, algo salió mal. Intenta nuevamente.";
    if (typeof error?.response?.data?.message === "string") {
      error_message = error.response.data.message;
    }
    messageApi.error(error_message);
    if (error?.response?.data?.success === false) {
      setAlert({
        visible: true,
        description: error_message,
      });
    }
  };

  const manage = async () => {
    setIsLoadingButton(true);
    setAlert({ visible: false, description: "" });

    const formValues = form.getFieldsValue();
    const payload: any = {
      name: formValues.name,
      unit: formValues.unit,
      stock: formValues.stock,
    };

    if (isEdit) {
      payload.id = id;
    }

    const mutation = isEdit
      ? updateIngredientMutation
      : createIngredientMutation;

    mutation.mutate(payload, {
      onSuccess: () => {
        messageApi.success("Ingrediente guardado correctamente.");
        if (!isEdit) {
          form.resetFields();
        }
        setOpen(false);
      },
      onError: handleError,
      onSettled: () => {
        setIsLoadingButton(false);
      },
    });
  };

  return (
    <Modal
      title={isEdit ? "Editar" : "Agregar"}
      centered
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      width={700}
    >
      {contextHolder}

      <Form
        form={form}
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
          inline: "center",
        }}
        layout="vertical"
        onFinish={manage}
      >
        {/* Name */}
        <Form.Item
          name="name"
          validateTrigger="onBlur"
          label="Nombre"
          rules={[{ required: true, max: 100, whitespace: true, min: 2 }]}
        >
          <Input placeholder="Ingresa nombre" />
        </Form.Item>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Category */}
          <Form.Item
            name="unit"
            label="Tipo unidad"
            rules={[{ required: true, message: "Selecciona un tipo" }]}
          >
            <Select placeholder="Selecciona un tipo" options={optionsTypes} />
          </Form.Item>

          {/* stock */}
          <Form.Item
            name="stock"
            label="Stock disponible"
            rules={[{ required: true, message: "Ingresa el stock" }]}
          >
            <InputNumber
              disabled={isEdit}
              min={0}
              max={999999999}
              controls={false}
              style={{ width: "100%" }}
              placeholder="Ingresa stock disponible"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
              }
              parser={(value: any) => value.replace(/\./g, "")}
            />
          </Form.Item>
        </div>

        {alert.visible && (
          <div className="mb-4">
            <Alert
              message="Error"
              description={alert.description}
              type="error"
              showIcon
            />
          </div>
        )}

        {/* Button */}
        <Form.Item className="flex justify-end items-end">
          <Space size="middle">
            <Button danger type="text" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoadingButton}>
              Guardar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
});
